import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasLongTravel } from '../skillUsage';

export const lightSweepPassSkill: CodeoSkill = {
    name: SKILL_NAMES.lightSweepPass,
    kind: 'codeo',
    description:
        'Pass a soft band of light diagonally across the frame once, catching every surface as it ' +
        'goes — the expensive-feeling pass that adds life to a still composition without moving it.',
    documentUsesSkill: check(
        (script, markup) =>
            /linear-gradient/.test(markup) && (/xPercent/.test(script) || hasLongTravel(script)),
    ),
    instructions: `# Light Sweep Pass

A soft diagonal band of light travels across the frame once and leaves. Nothing in the composition moves; only the lighting changes. It is the cheapest way to make a still layout feel manufactured rather than assembled, and it is the right answer when a graphic has landed and still needs something alive in it.

What sells it is that every surface catches the light *in step*. Give each surface its own sheen band and translate them all on one tween, and the frame reads as lit by a single moving source rather than as a sprite sliding over a picture.

## Markup

Each sheen is a wide, steeply skewed gradient strip clipped by the surface it belongs to. The stage-level band catches everything else.

\`\`\`html
<div id="lit-stage" class="absolute inset-0 overflow-hidden">
  <div class="absolute inset-0 flex flex-col justify-center items-start gap-10 p-24">
    <div id="lit-card" class="overflow-hidden" style="position: relative; width: 1200px; height: 480px; border-radius: 28px; background: var(--l5-color-1, #101820); box-shadow: 0 40px 90px rgba(0,0,0,0.35)">
      <div id="lit-headline" style="position: absolute; left: 64px; top: 180px; font-family: var(--l5-font-0), sans-serif; font-size: 84px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Built to last</div>
      <div class="sheen" style="position: absolute; top: -60%; left: -40%; width: 34%; height: 220%; background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0) 100%); rotate: 22deg;"></div>
    </div>
  </div>

  <!-- The stage band is backdrop geometry: it answers to the frame, not to the content, so it
       keeps its coordinates and says so. -->
  <div class="sheen" data-l5-absolute data-l5-allow-offscreen style="position: absolute; top: -60%; left: -40%; width: 26%; height: 220%; background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 100%); rotate: 22deg"></div>
</div>
\`\`\`

Use the standalone \`rotate\` property rather than \`transform: rotate(...)\` — the sheens are \`x\`-tweened, and a CSS \`transform\` on a tweened element would be replaced.

## Timeline code

One tween drives every sheen, so they cannot drift out of step. **The sweep is a finishing pass, not the composition** — the surface still has to arrive, and something still has to hold the frame after the light has left.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('.sheen', { xPercent: 0 });
gsap.set('#lit-card', { y: 26, opacity: 0 });
gsap.set('#lit-headline', { y: 14, opacity: 0 });

// The surface arrives first, so the light has something to catch.
tl.to('#lit-card', { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.1);
tl.to('#lit-headline', { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0.34);

// The pass itself.
tl.to('.sheen', { xPercent: 620, duration: 1.6, ease: 'power2.inOut' }, 0.95);

// A slow scale under everything, spanning the whole timeline, so the frame is
// never still once the light has gone.
tl.to('#lit-stage', { scale: 1.03, duration: 4.2, ease: 'none' }, 0);

window.__timelines = { main: tl };
\`\`\`

\`xPercent\` is relative to each sheen's own width, so bands of different widths still cross their surfaces together.

## Adapt

- **Angle**: 15°–30° reads as a key light from upper left. Past about 45° it reads as a horizontal scan, which is a more technological register.
- **Strength**: 0.06–0.16 alpha on a dark surface. On a light surface use a dark band (\`rgba(0,0,0,0.06)\`) instead — a white sheen on white is invisible.
- **Band width**: 34% of the surface is a soft key. Narrow to 10% for a hard specular streak across a logo or a metal edge; widen past 50% for a slow wash.
- **Resting key**: stop the tween partway (\`xPercent: 260\`) and the light settles on the composition rather than leaving it, which suits a graphic that ends on this card.
- **Second pass**: a faster pass late in the scene reads as a highlight rather than a lighting change. Never more than two.
- **Edge catch**: a 1px inset border on the surface, brightened on the same tween, makes the light appear to catch the card's edge as it crosses.

## Pitfalls

- **A sweep is not a composition.** If the only tween in the file is the sheen, the surface is already fully present at frame one and the frame is a still image on either side of the pass. The content has to arrive, and something has to carry the time after the light leaves.
- Don't start the sweep late. The timeline runs in the scene's own seconds, so a pass beginning at 0.85s leaves the opening 0.85s motionless — on a short scene that is most of it.
- Never build the band with CSS \`animation\`; it runs on wall-clock time and freezes when the composition is rasterized off-screen.
- Each sheen must be clipped by its surface (\`overflow: hidden\` on the card), or the band shows outside the object it is supposed to be lighting.
- The stage-level sheen extends past the frame by design, so it needs \`data-l5-allow-offscreen\`.
- Sheens are \`x\`-tweened, so none may carry a CSS \`transform\`. Use the standalone \`rotate\` property for the skew.
- A sweep over a busy photographic background is invisible. This needs flat, dark, or matte surfaces to read against.
- The slow stage scale above is a bed, not a second effect. Don't add a float loop or a particle field on top of it — see \`${SKILL_NAMES.ambientFloatLoop}\` for why one source of ambient motion is the limit.
- Keep it to a single pass in one direction. A sweep that returns reads as a glitch.`,
};
